import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Mail, Trash2, CheckCircle2, Circle, Inbox, RefreshCw, User, Calendar, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem("tk_admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState({ total: 0, unread: 0 });
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const admin = (() => {
    try { return JSON.parse(localStorage.getItem("tk_admin_user") || "{}"); } catch { return {}; }
  })();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [msgs, s] = await Promise.all([
        axios.get(`${API}/admin/messages`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/stats`, { headers: getAuthHeader() }),
      ]);
      setMessages(msgs.data);
      setStats(s.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("tk_admin_token");
        navigate("/admin/login");
      } else {
        toast.error("Failed to load messages");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const logout = () => {
    localStorage.removeItem("tk_admin_token");
    localStorage.removeItem("tk_admin_user");
    navigate("/admin/login");
  };

  const openMessage = async (m) => {
    setSelected(m);
    if (!m.read) {
      try {
        await axios.patch(`${API}/admin/messages/${m.id}/read`, {}, { headers: getAuthHeader() });
        setMessages((list) => list.map((x) => (x.id === m.id ? { ...x, read: true } : x)));
        setStats((s) => ({ ...s, unread: Math.max(0, s.unread - 1) }));
      } catch { /* silent */ }
    }
  };

  const deleteMessage = async (id) => {
    if (!confirm("Delete this message permanently?")) return;
    try {
      await axios.delete(`${API}/admin/messages/${id}`, { headers: getAuthHeader() });
      setMessages((list) => list.filter((m) => m.id !== id));
      setSelected(null);
      setStats((s) => ({ total: Math.max(0, s.total - 1), unread: s.unread }));
      toast.success("Message deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const formatDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    } catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="admin-dashboard">
      <div className="grain-overlay" />

      {/* Top bar */}
      <header className="border-b border-white/5 bg-[#0d0d0d]/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full border border-[#D4AF37]/40 flex items-center justify-center font-display font-bold text-[#D4AF37]">
              TK
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-display">Admin</p>
              <p className="text-sm text-white/80">{admin.email || "Dashboard"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchAll}
              className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-white/70 hover:text-[#D4AF37] hover:border-[#D4AF37]/40 transition-all"
              data-testid="admin-refresh"
              aria-label="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={() => navigate("/")}
              className="hidden sm:inline-flex text-xs uppercase tracking-[0.25em] font-display text-white/70 hover:text-[#D4AF37] transition-colors px-3"
              data-testid="admin-view-site"
            >
              View Site
            </button>
            <button
              onClick={logout}
              className="magnetic-btn"
              data-testid="admin-logout"
            >
              <LogOut className="w-3.5 h-3.5" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
          <StatCard label="Total Messages" value={stats.total} icon={Inbox} />
          <StatCard label="Unread" value={stats.unread} icon={Mail} accent />
          <StatCard label="Admin" value={admin.name || "Tariq"} icon={User} small />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* List */}
          <div className="lg:col-span-5 bg-[#161616] border border-white/5 rounded-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
              <h2 className="font-display uppercase text-sm tracking-[0.25em] text-white/80">Inbox</h2>
              <span className="text-xs text-white/40 font-display">{messages.length} total</span>
            </div>
            <div className="max-h-[70vh] overflow-y-auto" data-testid="admin-message-list">
              {loading && messages.length === 0 && (
                <div className="p-10 text-center text-white/50 text-sm">Loading messages...</div>
              )}
              {!loading && messages.length === 0 && (
                <div className="p-10 text-center text-white/50 text-sm" data-testid="admin-empty-state">
                  No messages yet. When someone contacts through the site, it will appear here.
                </div>
              )}
              {messages.map((m) => (
                <button
                  key={m.id}
                  onClick={() => openMessage(m)}
                  data-testid={`admin-message-${m.id}`}
                  className={`w-full text-left px-6 py-4 border-b border-white/5 flex items-start gap-3 hover:bg-white/5 transition-colors ${
                    selected?.id === m.id ? "bg-[#D4AF37]/5" : ""
                  }`}
                >
                  {m.read ? (
                    <CheckCircle2 className="w-4 h-4 text-white/25 mt-1 shrink-0" />
                  ) : (
                    <Circle className="w-4 h-4 text-[#D4AF37] fill-[#D4AF37] mt-1 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                      <p className={`font-display truncate ${m.read ? "text-white/70" : "text-white"}`}>
                        {m.name}
                      </p>
                      <span className="text-[10px] text-white/40 font-display shrink-0">
                        {formatDate(m.created_at).split(",")[0]}
                      </span>
                    </div>
                    <p className={`text-sm truncate ${m.read ? "text-white/50" : "text-white/85"}`}>
                      {m.subject}
                    </p>
                    <p className="text-xs text-white/40 truncate mt-0.5">{m.email}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-white/30 mt-1 shrink-0" />
                </button>
              ))}
            </div>
          </div>

          {/* Detail */}
          <div className="lg:col-span-7">
            {selected ? (
              <div className="bg-[#161616] border border-white/5 rounded-2xl p-8" data-testid="admin-message-detail">
                <div className="flex items-start justify-between gap-4 mb-6">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-display mb-2">
                      New Inquiry
                    </p>
                    <h2 className="font-display text-2xl md:text-3xl text-white leading-tight break-words">
                      {selected.subject}
                    </h2>
                  </div>
                  <button
                    onClick={() => deleteMessage(selected.id)}
                    className="w-10 h-10 rounded-full border border-red-500/30 text-red-400 hover:bg-red-500/10 flex items-center justify-center transition-colors"
                    data-testid="admin-delete-message"
                    aria-label="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                  <InfoRow icon={User} label="Name" value={selected.name} />
                  <InfoRow icon={Mail} label="Email" value={selected.email} isEmail />
                  <InfoRow icon={Calendar} label="Received" value={formatDate(selected.created_at)} />
                </div>

                <div className="rounded-xl bg-[#0A0A0A] border border-white/5 p-6">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-display mb-3">
                    Message
                  </p>
                  <p className="text-white/85 leading-relaxed whitespace-pre-wrap">
                    {selected.message}
                  </p>
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  <a
                    href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject)}`}
                    className="magnetic-btn solid"
                    data-testid="admin-reply"
                  >
                    <Mail className="w-4 h-4" />
                    Reply via Email
                  </a>
                </div>
              </div>
            ) : (
              <div className="bg-[#161616] border border-white/5 rounded-2xl p-16 text-center flex flex-col items-center justify-center min-h-[400px]">
                <div className="w-16 h-16 rounded-full border border-[#D4AF37]/25 bg-[#0A0A0A] flex items-center justify-center mb-4">
                  <Inbox className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <p className="font-display text-lg text-white mb-2">Select a message</p>
                <p className="text-sm text-white/50 max-w-sm">
                  Choose a submission from the inbox to view details and reply.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent, small }) {
  return (
    <div className={`rounded-2xl p-5 border ${accent ? "bg-[#D4AF37]/10 border-[#D4AF37]/30" : "bg-[#161616] border-white/5"}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-lg bg-[#0A0A0A] border border-[#D4AF37]/25 flex items-center justify-center">
          <Icon className="w-4 h-4 text-[#D4AF37]" />
        </div>
        <p className="text-[10px] uppercase tracking-[0.3em] text-white/60 font-display">{label}</p>
      </div>
      <p className={`font-display font-bold text-white ${small ? "text-lg" : "text-3xl"}`}>{value}</p>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, isEmail }) {
  return (
    <div className="rounded-xl bg-[#0A0A0A] border border-white/5 p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-3.5 h-3.5 text-[#D4AF37]" />
        <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 font-display">{label}</p>
      </div>
      {isEmail ? (
        <a href={`mailto:${value}`} className="text-white text-sm break-all hover:text-[#D4AF37] transition-colors">
          {value}
        </a>
      ) : (
        <p className="text-white text-sm break-words">{value}</p>
      )}
    </div>
  );
}
